import type { JSX } from 'react';
import { authService } from '../../services/auth.service.js';

interface Props {
  providers: ('google' | 'github')[];
  intent?: 'login' | 'connect';
  next?: string;
  layout?: 'stacked' | 'grid';
  onError?: (msg: string) => void;
}

const LABELS: Record<'google' | 'github', { label: string; bg: string; icon: JSX.Element }> = {
  google: {
    label: 'Google',
    bg: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    ),
  },
  github: {
    label: 'GitHub',
    bg: 'bg-gray-900 text-white border-gray-800 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-200 dark:hover:bg-white',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M12 0a12 12 0 0 0-3.79 23.4c.6.11.82-.26.82-.58v-2c-3.34.73-4.04-1.6-4.04-1.6-.55-1.4-1.34-1.78-1.34-1.78-1.1-.74.08-.73.08-.73 1.21.09 1.85 1.25 1.85 1.25 1.07 1.84 2.81 1.31 3.5 1 .11-.78.42-1.31.76-1.61-2.66-.3-5.47-1.34-5.47-5.94 0-1.31.47-2.38 1.24-3.22-.13-.3-.54-1.52.12-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.66.25 2.88.12 3.18.77.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.63-5.49 5.93.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 12 0z" />
      </svg>
    ),
  },
};

export function OAuthButtons({ providers, intent = 'login', next, layout = 'stacked', onError }: Props) {
  if (providers.length === 0) return null;

  const handleClick = (provider: 'google' | 'github') => {
    try {
      window.location.href = authService.oauthStartUrl(provider, intent, next);
    } catch (err: any) {
      onError?.(err?.message ?? 'OAuth başlatılamadı');
    }
  };

  const containerClass = layout === 'grid' ? 'grid grid-cols-2 gap-2' : 'flex flex-col gap-2';

  return (
    <div className={containerClass}>
      {providers.map((p) => {
        const cfg = LABELS[p];
        return (
          <button
            key={p}
            type="button"
            onClick={() => handleClick(p)}
            className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${cfg.bg}`}
          >
            {cfg.icon}
            {intent === 'connect' ? `${cfg.label} ile bağla` : `${cfg.label} ile devam et`}
          </button>
        );
      })}
    </div>
  );
}
